import React, { useEffect, useRef, useState, type FC } from 'react';
import { DownOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Badge, Spin, Switch, Tooltip, theme as antdTheme } from 'antd';
import { Button, DatePicker, Link, Space, Text, Title } from '@g2u/ui-kit';
import { format, isToday, isBefore, startOfDay, parse, isWithinInterval } from 'date-fns';
import { Swiper, SwiperSlide } from 'swiper/react';
import { A11y, Navigation } from 'swiper/modules';
import { isArray, isEmpty } from 'lodash';
import cx from 'classnames';

import {
  type TimeSlot,
  type JsonDataItem,
  type WithOptionalDataTest,
  SlotAvailability,
  type JsonData,
  ServiceAreaType,
  FullAvailabilityStatus,
} from 'types';
import { AvailabilityItem } from 'components';
import { isTimeExpired, parseDate } from 'helpers';
import { useText } from 'helpers/hooks/useText';
import 'swiper/scss';
import 'swiper/scss/a11y';
import 'swiper/scss/navigation';
import styles from './availability-calendar.module.scss';

export namespace AvailabilityCalendar {
  export type Props = {
    dayList?: JsonDataItem | null;
    loading: boolean;
    slot: { day: string; time: string } | null;
    technician: JsonDataItem | null;
    onSlotSelected: (slot: TimeSlot | null) => void;
    onTechnicianSelected: (tech: JsonDataItem | null) => void;
    onReachEnd: () => void;
    onReport?: () => void;
    hasAvailabilityOverrideToggle?: boolean;
    isAvailabilityOverrideEnabled?: boolean;
    onAvailabilityOverrideChange: (isEnabled: boolean) => void;
    timezone: string;
    dayPeriod: TimePeriods;
    skillSet?: string[];
  } & WithOptionalDataTest;

  export enum TimePeriods {
    All = 'All',
    Morning = 'Morning',
    Afternoon = 'Afternoon',
    Evening = 'Evening',
  }

  export const $: FC<Props> = (props) => {
    const {
      dayList,
      loading,
      dataTestId,
      onReachEnd,
      slot,
      technician,
      onSlotSelected,
      onTechnicianSelected,
      onReport,
      timezone,
      dayPeriod,
      skillSet,
      isAvailabilityOverrideEnabled,
      hasAvailabilityOverrideToggle,
      onAvailabilityOverrideChange,
    } = props;
    const [showSlotsPerDay, setShowSlotsPerDay] = useState<number>(6);
    const { getText } = useText();
    const { useToken } = antdTheme;
    const { token: theme } = useToken();
    const datePickerRef = useRef<any>(undefined);
    const isNoSkills =
      !skillSet ||
      (isArray(skillSet) && skillSet.length === 0) ||
      (isArray(skillSet) && skillSet.length === 1 && skillSet[0] === '');

    const getRibbonColor = (day: string) => {
      switch (day) {
        case 'Same Day':
          return 'volcano';
        case 'Week End':
          return 'green';
        case 'Holiday':
          return 'cyan';
        default:
          return 'purple';
      }
    };

    // 'See More' button
    const maxSlotsDay =
      dayList && !isEmpty(dayList?.appointments)
        ? Object.keys(dayList.appointments).reduce((a, b) =>
            dayList.appointments[a].length > dayList.appointments[b].length ? a : b,
          )
        : 0;

    // disable dates before today
    const datesBeforeNow = (current: Date | null) => {
      return !!current && isBefore(startOfDay(current), startOfDay(new Date()));
    };

    // filter slots by time period
    const filterAppointmentsByPeriod = (appointments: JsonData, period: TimePeriods) => {
      // Define time ranges for Morning, Afternoon, and Evening
      const timeRanges: Record<TimePeriods, { start: Date; end: Date }> = {
        [TimePeriods.Morning]: {
          start: parse('08:00:00', 'HH:mm:ss', new Date()),
          end: parse('11:59:59', 'HH:mm:ss', new Date()),
        },
        [TimePeriods.Afternoon]: {
          start: parse('12:00:00', 'HH:mm:ss', new Date()),
          end: parse('16:59:59', 'HH:mm:ss', new Date()),
        },
        [TimePeriods.Evening]: {
          start: parse('17:00:00', 'HH:mm:ss', new Date()),
          end: parse('20:00:00', 'HH:mm:ss', new Date()),
        },
        [TimePeriods.All]: {
          start: parse('00:00:00', 'HH:mm:ss', new Date()),
          end: parse('23:59:59', 'HH:mm:ss', new Date()),
        },
      };

      // Filter appointments based on the time ranges
      return appointments.filter((appointment) =>
        isWithinInterval(parse(appointment.time, 'HH:mm:ss', new Date()), timeRanges[period]),
      );
    };

    const handleOverrideMode = (isEnabled: boolean) => {
      onAvailabilityOverrideChange(isEnabled);
    };

    useEffect(() => {
      setShowSlotsPerDay(6);
    }, [dayList]);

    return (
      <div className={styles.availabilityCalendar} data-testid={dataTestId}>
        <Space.$
          direction={Space.Direction.Horizontal}
          justify={Space.Justify.SpaceBetween}
          className={styles.availabilityCalendar__sliderHeader}
        >
          <Text.$ strong>Availability Calendar</Text.$>
          <Space.$ size={Space.Size.Large}>
            {hasAvailabilityOverrideToggle && (
              <Space.$>
                <label htmlFor="full-calendar">View full calendar</label>
                <Switch
                  disabled={loading}
                  defaultValue={false}
                  id="full-calendar"
                  value={isAvailabilityOverrideEnabled}
                  onChange={handleOverrideMode}
                />
              </Space.$>
            )}
            <DatePicker.$
              name="jumpToDate"
              label="Jump to"
              colon
              disabledDate={datesBeforeNow}
              pickerRef={datePickerRef}
              disabled={loading}
            />
          </Space.$>
        </Space.$>
        {!isEmpty(dayList?.appointments) && (
          <>
            <Button.$
              className={cx(styles.availabilityCalendar__sliderButton, styles.availabilityCalendar__sliderButton_prev)}
              type={Button.Type.Text}
              dataTestId="prev-slide-btn"
              icon={<LeftOutlined />}
            />
            <Button.$
              className={cx(styles.availabilityCalendar__sliderButton, styles.availabilityCalendar__sliderButton_next)}
              type={Button.Type.Text}
              dataTestId="next-slide-btn"
              icon={<RightOutlined />}
              disabled={loading}
              loading={loading}
              style={{ pointerEvents: loading ? 'none' : 'all' }}
            />
          </>
        )}
        {loading && isEmpty(dayList?.appointments) ? (
          <Space.$ direction={Space.Direction.Vertical} align={Space.Align.Center}>
            <br />
            <Spin />
          </Space.$>
        ) : !isEmpty(dayList?.appointments) ? (
          <>
            <Swiper
              modules={[A11y, Navigation]}
              draggable={true}
              spaceBetween={10}
              className={styles.availabilityCalendar__slider}
              breakpoints={{
                [theme.screenXS]: {
                  slidesPerView: 1,
                },
                [theme.screenSM]: {
                  slidesPerView: 3,
                },
                [theme.screenLG]: {
                  slidesPerView: 3,
                },
                [theme.screenXL]: {
                  slidesPerView: 4,
                },
              }}
              navigation={{
                prevEl: `.${styles.availabilityCalendar__sliderButton_prev}`,
                nextEl: `.${styles.availabilityCalendar__sliderButton_next}`,
              }}
              onReachEnd={onReachEnd}
            >
              {dayList &&
                Object.keys(dayList.appointments).map((day) => {
                  const ribbon = dayList.appointmentDatesMetaInfo[day].info;
                  const dayData =
                    dayPeriod !== TimePeriods.All
                      ? filterAppointmentsByPeriod(dayList.appointments[day], dayPeriod)
                      : dayList.appointments[day];
                  return (
                    <SwiperSlide key={day} className={styles.availabilityCalendar__slide}>
                      <Space.$ className={styles.availabilityCalendar__slideHeader} align={Space.Align.Start}>
                        <div>
                          <Title.$ level={5} noMargin>
                            {isToday(parseDate(day)) ? 'Today' : format(parseDate(day), 'EEEE')}
                          </Title.$>
                          <Text.$>{format(parseDate(day), 'dd/MM/yyyy')}</Text.$>
                        </div>

                        {ribbon && (
                          <Tooltip title={ribbon.description}>
                            <div>
                              <Space.$>
                                <Badge.Ribbon
                                  text={ribbon.title}
                                  color={getRibbonColor(ribbon.title)}
                                  style={{ right: 2, top: 2, paddingLeft: 8, paddingRight: 8 }}
                                >
                                  <Space.$ direction={Space.Direction.Vertical} justify={Space.Justify.Start} compact>
                                    <Text.$ style={{ color: 'transparent', padding: '0 10px 4px' }}>
                                      {ribbon.title}
                                    </Text.$>
                                    <Text.$ size={Text.Size.SM} type="secondary" style={{ paddingLeft: 5 }}>
                                      Extra ${Math.ceil(ribbon.price)}
                                    </Text.$>
                                  </Space.$>
                                </Badge.Ribbon>
                              </Space.$>
                            </div>
                          </Tooltip>
                        )}
                      </Space.$>
                      {dayData.map((daySlot: JsonDataItem, idx: number) => {
                        const slotData = dayList.technicians[daySlot.technicianID];
                        if (idx < showSlotsPerDay) {
                          return (
                            <AvailabilityItem.$
                              isBest={idx === 0 && !isAvailabilityOverrideEnabled}
                              key={`${day}-${daySlot.time}-${daySlot.technicianID}`}
                              isActive={
                                day === slot?.day &&
                                daySlot.time === slot?.time &&
                                daySlot.technicianID.toString() === technician?.ID.toString()
                              }
                              time={daySlot.time}
                              id={daySlot.technicianID}
                              isConfirmationRequired={daySlot.availability === SlotAvailability.Confirmation}
                              isShortNotice={isTimeExpired(
                                `${format(parse(day, 'dd-MM-yyyy', new Date()), 'yyyy-MM-dd')} ${daySlot.time}`,
                                timezone,
                                2,
                              )}
                              techInfo={slotData}
                              slotInfo={daySlot}
                              isSurrounding={!!daySlot.surrounding_area_technician}
                              isOtherTimezone={!!daySlot.other_timezone_technician}
                              isSecondaryArea={daySlot.service_area_type === ServiceAreaType.Secondary}
                              isDisabled={
                                isAvailabilityOverrideEnabled &&
                                [FullAvailabilityStatus.OnSiteJob, FullAvailabilityStatus.RemoteJob].includes(
                                  daySlot.availability,
                                )
                              }
                              hasPopover={!isAvailabilityOverrideEnabled}
                              tag={isAvailabilityOverrideEnabled ? daySlot.availability : null}
                              onClick={(id, time) => {
                                onSlotSelected({
                                  day,
                                  time,
                                  techId: daySlot.technicianID,
                                  availability: daySlot.availability,
                                  isSurrounding: daySlot.surrounding_area_technician,
                                  isSecondaryArea: daySlot.service_area_type === ServiceAreaType.Secondary,
                                });
                                onTechnicianSelected(dayList.technicians[id]);
                              }}
                            />
                          );
                        }
                        return null;
                      })}
                    </SwiperSlide>
                  );
                })}

              {loading && (
                <SwiperSlide className={styles.availabilityCalendar__slide}>
                  <Space.$ direction={Space.Direction.Vertical} align={Space.Align.Center}>
                    <Spin />
                  </Space.$>
                </SwiperSlide>
              )}
            </Swiper>
            {dayList?.appointments[maxSlotsDay]?.length > showSlotsPerDay && (
              <Button.$
                type={Button.Type.Link}
                dataTestId="show-more-option"
                block
                onClick={() => {
                  setShowSlotsPerDay(showSlotsPerDay + 4);
                }}
              >
                See More <DownOutlined />
              </Button.$>
            )}
          </>
        ) : (
          <Space.$ align={Space.Align.Start} direction={Space.Direction.Vertical}>
            <Space.$ size={Space.Size.Large} compact direction={Space.Direction.Vertical} className="script-box">
              <Text.$>{getText('booking.technician.noSlotsIntro')}</Text.$>
              <Text.$>{getText('booking.technician.noSlotsOptions')}</Text.$>
              <br />
              <Text.$ type="danger">
                {isEmpty(dayList?.technicians)
                  ? isNoSkills
                    ? 'Sorry, there’re no techs service this location.'
                    : 'Sorry, there’re no techs with matching skillset(s) service this location.'
                  : 'Sorry, there’re no techs with matching skillset(s) are available.'}
              </Text.$>
              <Text.$ type="secondary" italic>
                *What you could do:*
              </Text.$>
              <Text.$ italic>
                <Link.$ underline onClick={() => datePickerRef?.current?.nativeElement.click()}>
                  &bull; Try other dates
                </Link.$>
              </Text.$>
              <Text.$ type="secondary" italic>
                &bull; Recommend remote jobs if customer’s problem can be solve remotely
              </Text.$>
              <Text.$ type="secondary" italic>
                &bull; Find other technicians by changing the skillset to &quot;Any&quot;, and{' '}
                <b>call them to confirm</b>.
              </Text.$>
            </Space.$>
            {onReport && (
              <Text.$>
                Lost customers?{' '}
                <Link.$ onClick={onReport} underline>
                  Record it here.
                </Link.$>
              </Text.$>
            )}
          </Space.$>
        )}
      </div>
    );
  };
}
